import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $minimalBalanceStore } from '@lib/store';
import { cn } from '@shared/utils';
import { AlertTypeEnum, RecipientDetail, type AccountByIdResponse } from '@shared/api';
import { AddRecipientModal } from '@widgets';
import { BaseInput, Button, Icon, Modal } from '@shared/widgets';
import CrossIcon from '@assets/icons/cross.svg';

interface ChangeAlertModalProps {
  account: AccountByIdResponse;
  show: boolean;
  onClose: () => void;
  symbol: string;
  initialValue?: number;
  initialRecipients?: RecipientDetail[];
  subId?: string;
}

export const ChangeAlertModal = (props: ChangeAlertModalProps) => {
  const minimalBalanceStore = useUnit($minimalBalanceStore.store);
  const recipients = minimalBalanceStore.recipient;

  const [value, setValue] = useState<string>(props.initialValue?.toString() || '');
  const [showAddModal, setShowAddModal] = useState<boolean>(false);

  const { t } = useTranslation('account');

  useEffect(() => {
    return () => $minimalBalanceStore.events.clear();
  }, []);

  useEffect(() => {
    $minimalBalanceStore.events.get(props.initialRecipients!);
  }, [props.initialRecipients, props.show]);

  const save = () => {
    const numberVal: number = parseInt(value);
    if (isNaN(numberVal)) return;
    if (!props.account) return;
    const minimalBalanceInfo = props.account.minimalBalanceAlertSubscription;
    if (minimalBalanceInfo && props.subId) {
      $minimalBalanceStore.events.updateMinimalBalance({
        subscriptionId: props.subId,
        accountId: props.account.id,
        recipients: recipients,
        minimalBalance: numberVal,
        override: true,
      });
    } else $minimalBalanceStore.events.addMinimalBalance({ recipients: recipients, minimalBalance: numberVal, accountId: props.account.id });
    props.onClose();
  };

  const closeModal = () => {
    props.onClose();
    props.initialValue && setValue(props.initialValue.toString());
  };

  return (
    <>
      <Modal isOpen={props.show} onClose={closeModal} title={t('sidebar.modals.changeAlert.title')}>
        <BaseInput
          className='mb-7 mt-2'
          label={t('sidebar.modals.changeAlert.placeholders.minimalBalance')}
          type='number'
          placeholder={t('sidebar.modals.changeAlert.placeholders.minimalBalance')}
          onlyPositive
          value={value}
          onChange={setValue}
        />
        <div className='flex flex-col gap-2'>
          {recipients.map((recipient, idx) => (
            <div key={idx} className='flex w-full items-center justify-between rounded-sm bg-white p-2 pl-3 dark:bg-slate'>
              <div className='shrink pr-4'>
                <div className='text-ellipsis text-sm'>{recipient.firstName}</div>
                <div className='text-ellipsis text-xs text-pearl-dark-gray'>{recipient.email}</div>
              </div>
              <Button className='p-0' color='icon' onClick={() => $minimalBalanceStore.events.remove(recipient.email!)}>
                <Icon icon={<CrossIcon className='h-6 w-6' />} color='red' />
              </Button>
            </div>
          ))}
        </div>
        <a className={cn('mt-2 w-fit cursor-pointer text-gothic underline', 'hover:no-underline')} onClick={() => setShowAddModal(true)}>
          {t('sidebar.modals.changeAlert.buttons.addNewRecipient')}
        </a>
        <Button className='mt-6' fullWidth color='primary' disabled={(props.subId === undefined && recipients.length === 0) || !value} onClick={save}>
          {t('buttons.confirm', { ns: 'common' })}
        </Button>
      </Modal>
      <AddRecipientModal show={showAddModal} onClose={() => setShowAddModal(false)} alertType={AlertTypeEnum.minimalBalance} />
    </>
  );
};
